import { useState, useEffect } from "react";
import { useAmbulanceAuth } from "@/contexts/AmbulanceAuthContext";
import AmbulanceLayout from "@/components/ambulance/AmbulanceLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { bridgeApi } from "@/lib/bridgeApi";
import KYCDocumentUpload from "@/components/ambulance/KYCDocumentUpload";
import { Shield, CheckCircle, Clock, XCircle, AlertTriangle } from "lucide-react";

const AmbulanceKycPage = () => {
  const { provider, providerToken, logout, refreshProvider } = useAmbulanceAuth();
  const { toast } = useToast();
  const [savingKyc, setSavingKyc] = useState(false);
  // Vehicle RC and Insurance intentionally left out of this provider-level KYC
  // flow — they're genuinely per-vehicle documents (each ambulance has its own
  // RC and insurance policy), so they belong on the Vehicles page against each
  // `ambulances` row, not here. That per-vehicle version isn't built yet;
  // rather than half-build it at the wrong (provider) level, this page only
  // covers the provider's own identity documents for now.
  const [kycDocs, setKycDocs] = useState({
    aadhaar_doc: "",
    aadhaar_doc_back: "",
    pan_doc: "",
    driving_license_doc: "",
    driving_license_doc_back: "",
  });

  const fetchKycDocs = async () => {
    if (!provider) return;
    try {
      const result = await bridgeApi.call<any>("ambulance_provider_get", { id: provider.id, token: providerToken });
      const data = result?.data || result;
      if (data) {
        setKycDocs({
          aadhaar_doc: data.aadhaar_doc || "",
          aadhaar_doc_back: data.aadhaar_doc_back || "",
          pan_doc: data.pan_doc || "",
          driving_license_doc: data.driving_license_doc || "",
          driving_license_doc_back: data.driving_license_doc_back || "",
        });
      }
    } catch {
      // silently fall back
    }
  };

  useEffect(() => {
    if (provider) fetchKycDocs();
  }, [provider]);

  const handleKycDocChange = (field: string, value: string) => {
    setKycDocs((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitKyc = async () => {
    if (!provider) return;
    if (!kycDocs.aadhaar_doc) {
      toast({
        title: "Required Document",
        description: "Aadhaar Card is mandatory for KYC",
        variant: "destructive",
      });
      return;
    }
    setSavingKyc(true);
    try {
      await bridgeApi.post("ambulance_provider_update", {
        id: provider.id,
        token: providerToken,
        // Not-submitted/rejected: always (re)enter review on click. Already
        // approved: don't force it — the backend only flips an approved
        // account back to pending if a document value actually changed, so
        // clicking this with nothing edited doesn't pointlessly re-queue it.
        ...(provider.kyc_status !== "approved" ? { kyc_status: "pending" } : {}),
        ...kycDocs,
      });
      await refreshProvider();
      toast({
        title: "KYC Submitted",
        description: "Your documents have been submitted for verification. You'll be notified once reviewed.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to submit KYC documents",
        variant: "destructive",
      });
    } finally {
      setSavingKyc(false);
    }
  };

  const getKycStatusInfo = () => {
    switch (provider?.kyc_status) {
      case "approved":
        return { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50 border-green-200", label: "Verified", description: "Your KYC has been approved. You are visible in public listings." };
      case "rejected":
        return { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200", label: "Rejected", description: "Your KYC was rejected. Please re-upload correct documents and resubmit." };
      case "pending":
        return { icon: Clock, color: "text-amber-600", bg: "bg-amber-50 border-amber-200", label: "Under Review", description: "Your documents are being reviewed. This usually takes 24-48 hours." };
      default:
        return { icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50 border-orange-200", label: "Not Submitted", description: "Submit your KYC documents to get verified and appear in public listings." };
    }
  };

  const kycInfo = getKycStatusInfo();
  const KycIcon = kycInfo.icon;

  return (
    <AmbulanceLayout provider={provider} onLogout={logout}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">KYC Status</h1>
          <p className="text-muted-foreground">Verify your identity and documents to appear in public listings</p>
        </div>

        {/* Status Overview */}
        <div className={`rounded-xl border p-4 flex items-center gap-4 ${kycInfo.bg}`}>
          <div className={`p-3 rounded-full bg-white/80 ${kycInfo.color}`}>
            <KycIcon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-lg">KYC Status:</span>
              <Badge className={
                provider?.kyc_status === "approved" ? "bg-green-100 text-green-700" :
                provider?.kyc_status === "rejected" ? "bg-red-100 text-red-700" :
                provider?.kyc_status === "pending" ? "bg-amber-100 text-amber-700" :
                "bg-orange-100 text-orange-700"
              }>
                {kycInfo.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{kycInfo.description}</p>
          </div>
        </div>

        {/* Rejection Reason Card */}
        {provider?.kyc_status === "rejected" && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
            <div className="flex items-center gap-2 text-red-700 font-semibold">
              <XCircle className="w-5 h-5" />
              KYC Rejected — Reason
            </div>
            <p className="text-sm text-red-600 font-medium">
              {provider?.rejection_reason || "Documents could not be verified. Please re-upload correct documents."}
            </p>
            <div className="mt-2">
              <p className="text-sm font-semibold text-red-700 mb-2">Aam karan (Common reasons):</p>
              <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                <li>Document blurry ya unclear hai — clear photo upload karo</li>
                <li>Document expire ho gaya hai — valid document use karo</li>
                <li>Naam ya details registration se match nahi kar rahe</li>
                <li>Wrong document upload hua (e.g. PAN ki jagah kuch aur)</li>
                <li>PDF corrupt hai ya open nahi ho raha</li>
              </ul>
            </div>
            <div className="pt-2 border-t border-red-200">
              <p className="text-sm text-red-700 font-medium">Ab kya karna hai:</p>
              <p className="text-sm text-red-600">Neeche documents re-upload karo aur "Submit KYC Documents" button dabao.</p>
            </div>
          </div>
        )}

        {/* Documents Card */}
        <Card className={`border ${kycInfo.bg}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              KYC Documents
            </CardTitle>
            <CardDescription>Upload clear, readable copies of the required documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <KYCDocumentUpload
                label="Aadhaar Card (Front)"
                documentType="Identity Proof"
                value={kycDocs.aadhaar_doc}
                onChange={(url) => handleKycDocChange("aadhaar_doc", url)}
                required
                providerId={provider?.id}
                fieldName="aadhaar_doc"
                token={providerToken}
                hint={provider?.kyc_status === "approved" ? "Verified ✓" : "e-Aadhaar (PDF) or front photo of the card"}
              />
              <KYCDocumentUpload
                label="Aadhaar Card (Back)"
                documentType="Identity Proof"
                value={kycDocs.aadhaar_doc_back}
                onChange={(url) => handleKycDocChange("aadhaar_doc_back", url)}
                providerId={provider?.id}
                fieldName="aadhaar_doc_back"
                token={providerToken}
                hint={provider?.kyc_status === "approved" ? "Verified ✓" : "Only for a physical/PVC card — skip if you uploaded e-Aadhaar"}
              />
              <KYCDocumentUpload
                label="PAN Card"
                documentType="Tax ID"
                value={kycDocs.pan_doc}
                onChange={(url) => handleKycDocChange("pan_doc", url)}
                providerId={provider?.id}
                fieldName="pan_doc"
                token={providerToken}
                hint={provider?.kyc_status === "approved" ? "Verified ✓" : "Clear photo of PAN card"}
              />
              <KYCDocumentUpload
                label="Driving License (Front)"
                documentType="License"
                value={kycDocs.driving_license_doc}
                onChange={(url) => handleKycDocChange("driving_license_doc", url)}
                providerId={provider?.id}
                fieldName="driving_license_doc"
                token={providerToken}
                hint={provider?.kyc_status === "approved" ? "Verified ✓" : "Valid driving license"}
              />
              <KYCDocumentUpload
                label="Driving License (Back)"
                documentType="License"
                value={kycDocs.driving_license_doc_back}
                onChange={(url) => handleKycDocChange("driving_license_doc_back", url)}
                providerId={provider?.id}
                fieldName="driving_license_doc_back"
                token={providerToken}
                hint={provider?.kyc_status === "approved" ? "Verified ✓" : "Back side, if your license has details on both sides"}
              />
            </div>

            {provider?.kyc_status === "approved" && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 dark:text-green-400 text-center">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  All documents verified.
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 text-center mt-1">
                  Uploading a replacement document below will send your account back for re-verification — it won't show as "Verified" again until admin reviews the new document.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSubmitKyc}
                disabled={savingKyc || !kycDocs.aadhaar_doc}
                className="bg-gradient-to-r from-amber-500 to-orange-500"
              >
                <Shield className="w-4 h-4 mr-2" />
                {savingKyc
                  ? "Submitting..."
                  : provider?.kyc_status === "approved"
                  ? "Save Document Changes"
                  : provider?.kyc_status === "rejected"
                  ? "Re-submit KYC Documents"
                  : "Submit KYC Documents"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AmbulanceLayout>
  );
};

export default AmbulanceKycPage;
